import React from 'react';
import { X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { useFeatureFlagEnabled } from '@cotera/client/app/hooks/use-feature-flag';
import { ListFilterMenu } from '../resources/list/list-filter-menu';
import { cn } from '@cotera/client/v0/lib/utils';
import { FilterChipGroup } from '../resources/list/filter-chip-group';
import { LibraryViewToggle } from './library-view-toggle';
import type { LibraryViewMode } from './folder-contents-page.model';

/**
 * Top-level Library type filter chips. "All" shows folders + loose items; a
 * type view flattens all items of that type across folders. Inside a folder the
 * same chips narrow that folder's contents (server-side, via `itemTypes`).
 */
export type TypeFilter = 'all' | 'agent' | 'document' | 'workflow' | 'dataset';

/**
 * Ownership lens over the Library, folding in the old "Shared with me" screen:
 * "mine" = I own it, "shared" = someone else owns it (an inbound share).
 */
export type OwnershipFilter = 'all' | 'mine' | 'shared';

export function isTypeFilter(value: string): value is TypeFilter {
  return (
    value === 'all' ||
    value === 'agent' ||
    value === 'document' ||
    value === 'workflow' ||
    value === 'dataset'
  );
}

export function isOwnershipFilter(value: string): value is OwnershipFilter {
  return value === 'all' || value === 'mine' || value === 'shared';
}

const TYPE_CHIPS: { value: Exclude<TypeFilter, 'all'>; label: string }[] = [
  { value: 'agent', label: 'Agents' },
  { value: 'document', label: 'Documents' },
  { value: 'workflow', label: 'Workflows' },
];

const DATASET_CHIP = { value: 'dataset', label: 'Datasets' } as const;

/**
 * The type lenses on offer, `All` excluded — one derivation for both shapes of
 * the filter.
 *
 * Datasets are behind a flag, and the two used to ask about it differently: the
 * chip row wrapped its chip in `<FeatureFlagged>` while the menu read the hook.
 * Two answers to one question is one answer too many, and the menu could not
 * use the wrapper anyway — a dropdown takes an array, not JSX.
 */
const useTypeChipOptions = (): { value: TypeFilter; label: string }[] => {
  const datasetsEnabled = useFeatureFlagEnabled('datasets');
  return datasetsEnabled ? [...TYPE_CHIPS, DATASET_CHIP] : TYPE_CHIPS;
};

const OWNERSHIP_OPTIONS: { value: OwnershipFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Owned by me' },
  { value: 'shared', label: 'Shared with me' },
];

/**
 * The Library's filters as they appear on a phone: one menu, on the title's
 * row, instead of a band of chips plus a second row for the ownership lens.
 *
 * See {@link ListFilterMenu} for why the chips give way at all.
 */
export const LibraryFiltersMenu: React.FC<{
  typeFilter: TypeFilter;
  onTypeFilterChange: (value: string) => void;
  ownershipFilter: OwnershipFilter;
  onOwnershipFilterChange: (value: string) => void;
  showOwnership: boolean;
  filtersActive: boolean;
  onClearFilters: () => void;
}> = ({
  typeFilter,
  onTypeFilterChange,
  ownershipFilter,
  onOwnershipFilterChange,
  showOwnership,
  filtersActive,
  onClearFilters,
}) => {
  const chipOptions = useTypeChipOptions();
  // The menu lists `All` alongside the rest; the chip band draws it separately.
  const typeOptions = [{ value: 'all' as const, label: 'All' }, ...chipOptions];

  return (
    <ListFilterMenu
      active={filtersActive}
      onClear={onClearFilters}
      groups={[
        {
          label: 'Type',
          value: typeFilter,
          options: typeOptions,
          onChange: onTypeFilterChange,
        },
        ...(showOwnership
          ? [
              {
                label: 'Owner',
                value: ownershipFilter,
                options: OWNERSHIP_OPTIONS,
                onChange: onOwnershipFilterChange,
              },
            ]
          : []),
      ]}
    />
  );
};

/**
 * The Library's type band. Its own component only because it needs a hook and
 * {@link LibraryFilters} below is an expression.
 */
const LibraryTypeChips: React.FC<{
  value: TypeFilter;
  onValueChange: (value: string) => void;
}> = ({ value, onValueChange }) => {
  const options = useTypeChipOptions();
  return (
    <FilterChipGroup
      value={value}
      onValueChange={onValueChange}
      options={options}
      ariaLabel="Filter library by type"
    />
  );
};

export const LibraryFilters: React.FC<{
  typeFilter: TypeFilter;
  onTypeFilterChange: (value: string) => void;
  ownershipFilter: OwnershipFilter;
  onOwnershipFilterChange: (value: string) => void;
  /**
   * Ownership is a top-level-only lens: the folder-contents endpoint takes no
   * ownership parameter, so offering the control inside a folder would render a
   * filter that silently does nothing.
   */
  showOwnership: boolean;
  /** Any filter is narrowing the list, so offer a one-click reset. */
  filtersActive: boolean;
  onClearFilters: () => void;
  viewMode: LibraryViewMode;
  onViewModeChange: (value: LibraryViewMode) => void;
}> = ({
  typeFilter,
  onTypeFilterChange,
  ownershipFilter,
  onOwnershipFilterChange,
  showOwnership,
  filtersActive,
  onClearFilters,
  viewMode,
  onViewModeChange,
}) => (
  // mb-5 rather than mb-3: the chips sat almost flush against the content
  // beneath them, so the filter row read as part of the list rather than as the
  // control band above it.
  <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
    {/* "All" browses by structure; the type views flatten across folders — the
        divider `FilterChipGroup` draws signals they're a different kind of
        view. */}
    <LibraryTypeChips value={typeFilter} onValueChange={onTypeFilterChange} />
    <div className="flex shrink-0 items-center gap-2">
      {/* Rendered only while something is actually narrowing the list. It used
          to be permanently present (just transparent), which reserved space in
          the band for a control that is irrelevant most of the time. */}
      {filtersActive ? (
        <button
          type="button"
          onClick={onClearFilters}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs',
            'text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
            'cursor-pointer'
          )}
        >
          <X className="h-3 w-3" aria-hidden="true" />
          Clear filters
        </button>
      ) : null}
      {showOwnership ? (
        // Ownership lens — the old "Shared with me" screen, folded in as a filter.
        <Select value={ownershipFilter} onValueChange={onOwnershipFilterChange}>
          <SelectTrigger
            size="sm"
            className="w-max"
            aria-label="Filter library by owner"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="mine">Owned by me</SelectItem>
            <SelectItem value="shared">Shared with me</SelectItem>
          </SelectContent>
        </Select>
      ) : null}
      <LibraryViewToggle
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
      />
    </div>
  </div>
);
